import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    STROOP_COLORS,
    COLOR_BLIND_STROOP_COLORS,
    createStroopRound,
    BrainTrainingGameIcon,
    GameResultOverlay,
    GameShell,
} from '../shared';

function StroopGame({
    colorBlindModeEnabled = false,
    copy = {},
    initialLevel = 3,
    onRunComplete = null,
    roundLimit = 12,
    showResults = true,
    ...props
}) {
    const timerRef = useRef(null);
    const completionReportedRef = useRef(false);
    const level = Math.max(1, Math.min(3, Math.round(Number(initialLevel) || 3)));
    const stroopColors = useMemo(() => (
        (colorBlindModeEnabled ? COLOR_BLIND_STROOP_COLORS : STROOP_COLORS).map((color) => ({
            ...color,
            label: copy.colors?.[color.id] || color.label,
        })).slice(0, level + 1)
    ), [colorBlindModeEnabled, copy.colors, level]);
    const [round, setRound] = useState(1);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [trial, setTrial] = useState(() => createStroopRound(stroopColors));
    const [choice, setChoice] = useState('');
    const maxRounds = Math.max(1, Math.round(Number(roundLimit) || 12));
    const done = round > maxRounds;
    const correctChoice = choice && choice === trial.word.id;
    const wordLength = Array.from(`${trial.word.label || ''}`).length;
    const wordSizeClass = wordLength >= 10
        ? 'is-extra-long'
        : wordLength >= 8
            ? 'is-long'
            : wordLength >= 6
                ? 'is-medium'
                : '';

    const reset = () => {
        window.clearTimeout(timerRef.current);
        completionReportedRef.current = false;
        setRound(1);
        setScore(0);
        setStreak(0);
        setTrial(createStroopRound(stroopColors));
        setChoice('');
    };

    useEffect(() => {
        reset();
        return () => window.clearTimeout(timerRef.current);
    }, [colorBlindModeEnabled]);

    useEffect(() => {
        if (!done || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.({ level, maxScore: maxRounds, score, streak });
    }, [done, level, maxRounds, onRunComplete, score, streak]);

    const choose = (colorId) => {
        if (done || choice) {
            return;
        }

        const correct = colorId === trial.word.id;
        const nextStreak = correct ? streak + 1 : 0;
        setChoice(colorId);
        setScore((value) => value + (correct ? 1 : 0));
        setStreak(nextStreak);
        timerRef.current = window.setTimeout(() => {
            const nextRound = round + 1;
            setRound(nextRound);
            setChoice('');
            if (nextRound <= maxRounds) {
                setTrial(createStroopRound(stroopColors));
            }
        }, correct ? 600 : 850);
    };

    const metrics = { level, maxScore: maxRounds, score, streak };

    return (
        <GameShell {...props} copy={copy} metrics={metrics} tone={correctChoice || done ? 'good' : choice ? 'miss' : 'ready'}>
            <div
                className={`UFOV-brain-games__stroop-word ${done ? '' : wordSizeClass}`.trim()}
                style={{ color: trial.ink.hex }}
            >
                {done ? <BrainTrainingGameIcon icon="check" /> : trial.word.label}
            </div>
            <div className="UFOV-brain-games__game-cue">{copy.stroopCue}</div>
            <div className="UFOV-brain-games__color-grid">
                {stroopColors.map((color) => (
                    <button
                        aria-label={color.label}
                        className={`UFOV-brain-games__color-button ${choice === color.id ? 'is-picked' : ''} ${choice && color.id === trial.word.id ? 'is-correct' : ''}`.trim()}
                        disabled={done || Boolean(choice)}
                        key={color.id}
                        onClick={() => choose(color.id)}
                        style={{ '--swatch': color.hex }}
                        type="button"
                    />
                ))}
            </div>
            {done && showResults ? <GameResultOverlay metrics={metrics} onRestart={reset} /> : null}
        </GameShell>
    );
}

export default StroopGame;
